import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

/* ---------------------------------------------------------------------------
 * Theme system — Light / Dark / Hybrid / Hybrid Dark.
 *
 * The app is built on shadcn class-based theming: adding `.dark` to <html>
 * swaps every color token. `:root` holds the warm-cream LIGHT palette and
 * `.dark` holds the signature gold-on-black DARK palette (see index.css).
 *
 * The four workspace themes (login + portals stay ForceDark):
 *   - light        cream chrome + cream canvas + cream cards        (root: light)
 *   - dark         gold-on-black everywhere                         (root: .dark)
 *   - hybrid       dark chrome + cream canvas + cream cards         (root: light,
 *                  chrome subtrees get `.dark` injected in the layout)
 *   - hybrid-dark  dark chrome + cream canvas + dark cards          (root: .dark,
 *                  the content region's canvas is overridden to cream in CSS)
 *
 * `theme` is the user's CHOICE; `resolved` ('light' | 'dark') follows the root
 * `.dark` state (dark for dark + hybrid-dark, light for light + hybrid) so
 * consumers like the Toaster read on the right ground. A `data-theme` attribute
 * carries the exact choice for the Hybrid Dark canvas override in index.css.
 * The choice persists in localStorage.
 * ------------------------------------------------------------------------- */

const STORAGE_KEY = 'kocore-theme';

const THEMES = ['light', 'dark', 'hybrid', 'hybrid-dark'];
const DEFAULT_THEME = 'dark'; // brand signature for first-run / no preference

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  resolved: 'dark',
  setTheme: () => {},
});

export const getStoredTheme = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (THEMES.includes(v)) return v;
  } catch {
    /* localStorage unavailable — fall through */
  }
  return DEFAULT_THEME; // legacy 'system' and anything unknown fall back here
};

/** True when the ROOT should carry `.dark` (dark palette base). */
const rootIsDark = (theme) => theme === 'dark' || theme === 'hybrid-dark';

// A page that must look one way for everyone (the client portal is light
// for every homeowner, whatever this browser's saved theme) pins the ROOT
// here. Module-level on purpose: ThemeProvider's own effect runs AFTER the
// page's layout effect and would otherwise put `.dark` straight back.
let rootOverride = null;

/** Apply a theme choice to <html>, returning the resolved 'light'|'dark'. */
export const applyThemeClass = (theme) => {
  const effective = rootOverride || theme;
  const dark = rootIsDark(effective);
  if (typeof document !== 'undefined') {
    const el = document.documentElement;
    el.classList.toggle('dark', dark);
    el.setAttribute('data-theme', effective);
    el.style.colorScheme = dark ? 'dark' : 'light';
  }
  return dark ? 'dark' : 'light';
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(getStoredTheme);
  const [resolved, setResolved] = useState(() =>
    rootIsDark(theme) ? 'dark' : 'light'
  );

  const setTheme = useCallback((next) => {
    const value = THEMES.includes(next) ? next : DEFAULT_THEME;
    setThemeState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore persistence failure */
    }
    setResolved(applyThemeClass(value));
  }, []);

  // Keep <html> in sync whenever the choice changes.
  useEffect(() => {
    setResolved(applyThemeClass(theme));
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

/**
 * ForceDark — pins a subtree to the dark palette regardless of the app theme.
 * Used for the "front door" (login / password / portals) so the branded
 * gold-on-black first impression is preserved even when the workspace is light.
 */
/**
 * ForcePortalLight — the client portal reads light for every homeowner
 * (John, 2026-10-08: "my dash is on light theme — when I preview the client
 * portal it reads in dark"). Pins the ROOT to light before first paint, not
 * just a wrapper, so Tailwind `dark:` variants and Radix popovers (portaled
 * to <body>) are light too; restores the visitor's own theme on the way out.
 * The portal's header opts back into `.dark` for the black-and-gold bar.
 */
export const ForcePortalLight = ({ children }) => {
  useLayoutEffect(() => {
    rootOverride = 'light';
    applyThemeClass('light');
    return () => {
      rootOverride = null;
      applyThemeClass(getStoredTheme());
    };
  }, []);
  return <div style={{ colorScheme: 'light' }}>{children}</div>;
};

export const ForceDark = ({ children }) => (
  <div className="dark" style={{ colorScheme: 'dark' }}>
    {children}
  </div>
);
